import { Copy, Download, ExternalLink, Presentation } from "lucide-react";
import { ShowBase, useNotify, useRecordContext, useTranslate } from "ra-core";
import { Link } from "react-router";
import { EditButton } from "@/components/admin/edit-button";
import { ReferenceArrayField } from "@/components/admin/reference-array-field";
import { SingleFieldList } from "@/components/admin/single-field-list";
import { TextField } from "@/components/admin/text-field";
import { buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { findChoice, STYLE_STATUSES } from "../common/constants";
import { HubPage, SectionCard, StatusBadge } from "../common/components";
import { ShareLinkButton } from "../portal/ShareLinkButton";
import type { Style } from "../types";
import { StylePreview, useStyleFonts } from "./StylePreview";
import {
  COLOR_KEYS,
  resolveTokens,
  toCssVariables,
  toShadcnTheme,
} from "./tokens";

export const StyleShow = () => (
  <ShowBase resource="styles">
    <StyleShowContent />
  </ShowBase>
);

const StyleShowContent = () => {
  const style = useRecordContext<Style>();
  const translate = useTranslate();
  const notify = useNotify();
  if (!style) return null;
  const t = resolveTokens(style.tokens);

  const copy = async (text: string) => {
    await navigator.clipboard?.writeText(text).catch(() => {});
    notify("hub.common.copied");
  };
  const download = (text: string, name: string) => {
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };
  const slug = style.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  return (
    <HubPage
      className="max-w-6xl"
      title={
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold">{style.name}</h1>
            <StatusBadge choice={findChoice(STYLE_STATUSES, style.status)} />
          </div>
          {style.description && (
            <p className="max-w-2xl text-sm text-muted-foreground">
              {style.description}
            </p>
          )}
        </div>
      }
      actions={
        <>
          <Link
            to={`/styles/${style.id}/present`}
            className={buttonVariants({ size: "sm" })}
          >
            <Presentation className="size-4" />
            {translate("hub.styles.present")}
          </Link>
          <ShareLinkButton kind="style" styleId={style.id} />
          <DropdownMenu>
            <DropdownMenuTrigger
              className={buttonVariants({ size: "sm", variant: "outline" })}
            >
              <Download className="size-4" />
              {translate("hub.styles.export")}
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() => copy(toCssVariables(style.tokens))}
              >
                <Copy className="size-4" />
                {translate("hub.styles.export_css")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => copy(toShadcnTheme(style.tokens))}
              >
                <Copy className="size-4" />
                {translate("hub.styles.export_tailwind")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() =>
                  download(
                    JSON.stringify(style.tokens, null, 2),
                    `${slug}.tokens.json`,
                  )
                }
              >
                <Download className="size-4" />
                {translate("hub.styles.export_json")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <EditButton />
        </>
      }
    >
      <div className="space-y-4">
        <StylePreview tokens={style.tokens} />
        <div className="grid gap-4 md:grid-cols-2">
          <SectionCard title={translate("hub.styles.colors")}>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {COLOR_KEYS.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => copy(t.colors[key])}
                  className="overflow-hidden rounded-lg border text-left"
                >
                  <span
                    className="block h-12"
                    style={{ background: t.colors[key] }}
                  />
                  <span className="block px-2 py-1 text-xs">
                    {translate(`hub.styles.color.${key}`)}
                    <span className="block font-mono text-muted-foreground">
                      {t.colors[key]}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </SectionCard>
          <Typography style={style} />
        </div>
        {!!style.images?.length && (
          <SectionCard title={translate("resources.styles.fields.images")}>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {style.images.map((image) => (
                <a
                  key={image.src}
                  href={image.src}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src={image.src}
                    alt={image.title ?? style.name}
                    className="rounded-lg border"
                  />
                </a>
              ))}
            </div>
          </SectionCard>
        )}
        <div className="flex flex-wrap gap-6 text-sm">
          {style.demo_url && (
            <a
              href={style.demo_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 hover:underline"
            >
              <ExternalLink className="size-4" />
              {translate("resources.styles.fields.demo_url")}
            </a>
          )}
          {!!style.deal_ids?.length && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">
                {translate("resources.styles.fields.deal_ids")}:
              </span>
              <ReferenceArrayField source="deal_ids" reference="deals">
                <SingleFieldList>
                  <TextField source="name" />
                </SingleFieldList>
              </ReferenceArrayField>
            </div>
          )}
        </div>
      </div>
    </HubPage>
  );
};

const Typography = ({ style }: { style: Style }) => {
  const translate = useTranslate();
  const t = resolveTokens(style.tokens);
  useStyleFonts([t.fonts.heading, t.fonts.body]);
  return (
    <SectionCard title={translate("hub.styles.typography")}>
      <div className="space-y-3">
        <div>
          <p className="text-xs text-muted-foreground">
            {translate("hub.styles.heading_font")} · {t.fonts.heading}
          </p>
          <p
            className="text-3xl font-semibold"
            style={{ fontFamily: `"${t.fonts.heading}", system-ui` }}
          >
            Agenda de la clínica
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">
            {translate("hub.styles.body_font")} · {t.fonts.body}
          </p>
          <p style={{ fontFamily: `"${t.fonts.body}", system-ui` }}>
            Gestiona citas, pacientes y facturación desde un único lugar, con
            recordatorios automáticos.
          </p>
        </div>
        <p className="text-xs text-muted-foreground">
          {translate("hub.styles.radius")}: {t.radius} ·{" "}
          {translate("hub.styles.mode")}: {translate(`hub.styles.${t.mode}`)}
        </p>
      </div>
    </SectionCard>
  );
};
